import React, { Dispatch, FC, memo, SetStateAction, useContext, useMemo, useState } from 'react';
import { ProjectsContext } from 'src/app/providers/ProjectProvider';
import ProjectsService from 'src/shared/api/projects/ProjectsService';
import { setError as setGlobalError, setSuccess as setGlobalSuccess } from 'src/shared/lib/utils';
import { FormTotalsRowOperation, FormTotalsRowSettings } from 'src/shared/store/projects/types';
import { Button, Modal } from 'src/shared/ui';

interface ProjectTotalsRowModalProps {
  setIsOpenTotalsRowModal: Dispatch<SetStateAction<boolean>>;
}

const operationOptions: Array<{ value: '' | FormTotalsRowOperation; label: string }> = [
  { value: '', label: 'Не считать' },
  { value: 'sum', label: 'СУММ' },
  { value: 'avg', label: 'СРЗНАЧ' },
  { value: 'count', label: 'СЧЁТ' },
  { value: 'counta', label: 'СЧЁТЗ' },
  { value: 'max', label: 'МАКС' },
  { value: 'min', label: 'МИН' },
  { value: 'product', label: 'ПРОИЗВЕД' },
];

export const ProjectTotalsRowModal: FC<ProjectTotalsRowModalProps> = memo(
  ({ setIsOpenTotalsRowModal }) => {
    const { tableSettings, setTableSettings } = useContext(ProjectsContext);
    const [isSaving, setIsSaving] = useState(false);

    const totalsRowSettings = tableSettings?.viewSettings?.totalsRow;
    const hasConfiguredTotalsRow = Boolean(
      totalsRowSettings?.enabled && totalsRowSettings?.columns?.length,
    );

    const eligibleColumns = useMemo(
      () =>
        (tableSettings?.formStructure || []).filter(
          field => field.columnType !== 'actions' && field.columnName.toLowerCase() !== 'id',
        ),
      [tableSettings?.formStructure],
    );

    const [operations, setOperations] = useState<Record<string, '' | FormTotalsRowOperation>>(() =>
      eligibleColumns.reduce<Record<string, '' | FormTotalsRowOperation>>((acc, field) => {
        const existingOperation = totalsRowSettings?.columns?.find(
          column => column.columnName === field.columnName,
        )?.operation;
        acc[field.columnName] = existingOperation || '';
        return acc;
      }, {}),
    );

    const onClose = () => setIsOpenTotalsRowModal(false);

    const handleOperationChange = (columnName: string, operation: '' | FormTotalsRowOperation) => {
      setOperations(prev => ({
        ...prev,
        [columnName]: operation,
      }));
    };

    const persistViewSettings = async (
      nextViewSettings: NonNullable<typeof tableSettings>['viewSettings'],
      successMessage: string,
    ) => {
      if (!tableSettings) {
        return;
      }

      await ProjectsService.updateForm({
        id: tableSettings.id,
        formName: tableSettings.formName,
        formDescription: tableSettings.formDescription,
        appearanceSettings: tableSettings.appearanceSettings || {},
        viewSettings: nextViewSettings,
        businessRules: tableSettings.businessRules || [],
        flkRules: tableSettings.flkRules || [],
        workflowSettings: tableSettings.workflowSettings,
        currentStatus: tableSettings.workflowCurrentStatus,
      });

      setTableSettings({
        ...tableSettings,
        viewSettings: nextViewSettings,
      });

      setGlobalSuccess(successMessage);
      setIsOpenTotalsRowModal(false);
    };

    const handleSave = async () => {
      if (!tableSettings) {
        return;
      }

      const columns = eligibleColumns
        .map(field => {
          const operation = operations[field.columnName];
          if (!operation) {
            return null;
          }

          return {
            columnName: field.columnName,
            operation,
          };
        })
        .filter(Boolean) as NonNullable<FormTotalsRowSettings['columns']>;

      if (!columns.length) {
        setGlobalError('Выберите хотя бы одну операцию для строки итогов');
        return;
      }

      const nextTotalsRow: FormTotalsRowSettings = {
        enabled: true,
        label: totalsRowSettings?.label ?? 'Итоги',
        position: totalsRowSettings?.position ?? 'end',
        columns,
      };
      const nextViewSettings = {
        ...(tableSettings.viewSettings || {}),
        totalsRow: nextTotalsRow,
      };

      setIsSaving(true);

      try {
        await persistViewSettings(nextViewSettings, 'Настройки строки итогов сохранены');
      } catch (error: any) {
        if (error?.response?.data?.error) {
          setGlobalError(error.response.data.error);
        } else {
          setGlobalError('Не удалось сохранить настройки строки итогов');
        }
      } finally {
        setIsSaving(false);
      }
    };

    const handleDelete = async () => {
      if (!tableSettings || !hasConfiguredTotalsRow) {
        return;
      }

      const nextViewSettings = { ...(tableSettings.viewSettings || {}) };
      delete nextViewSettings.totalsRow;

      setIsSaving(true);

      try {
        await persistViewSettings(nextViewSettings, 'Строка итогов удалена');
      } catch (error: any) {
        if (error?.response?.data?.error) {
          setGlobalError(error.response.data.error);
        } else {
          setGlobalError('Не удалось удалить строку итогов');
        }
      } finally {
        setIsSaving(false);
      }
    };

    return (
      <Modal onClose={onClose}>
        <div className="flex w-[56rem] flex-col items-center">
          <div className="font-gothampro-500 mb-5 w-fit text-xl text-green-100">Строка итогов</div>

          <div className="w-full rounded border border-blue-400 p-4">
            <div className="mb-3 text-xs text-grey-300">
              Выберите операцию для каждой колонки, которую нужно включить в строку итогов.
            </div>

            <div className="space-y-3">
              {eligibleColumns.map(field => (
                <div key={field.columnName} className="grid grid-cols-12 items-center gap-3">
                  <div className="col-span-6 text-xs dark:text-white">{field.columnLabel}</div>
                  <select
                    aria-label={`Итог для колонки ${field.columnLabel}`}
                    value={operations[field.columnName] || ''}
                    onChange={event =>
                      handleOperationChange(
                        field.columnName,
                        event.target.value as '' | FormTotalsRowOperation,
                      )
                    }
                    className="col-span-6 rounded border border-violet-400 bg-white p-2 text-xs dark:border-violet-900 dark:bg-violet-1200 dark:text-white"
                  >
                    {operationOptions.map(option => (
                      <option
                        key={`${field.columnName}-${option.value || 'none'}`}
                        value={option.value}
                      >
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-5 flex w-full items-center gap-3">
            {hasConfiguredTotalsRow && (
              <Button variant="orange" onClick={handleDelete} disabled={isSaving}>
                Удалить строку итогов
              </Button>
            )}
            <Button variant="green" onClick={handleSave} disabled={isSaving}>
              {isSaving ? 'Сохранение...' : 'Сохранить'}
            </Button>
            <Button variant="greyLight" onClick={onClose}>
              Отменить
            </Button>
          </div>
        </div>
      </Modal>
    );
  },
);
